import type { Feature, GeoJsonProperties, Polygon } from "geojson";

const EARTH_RADIUS_MILES = 3958.7613;

// GeoJsonProperties is `Record<string, any> | null`; drop the null and keep the index signature.
export type CircleFeatureProperties = NonNullable<GeoJsonProperties> & {
  preceptorId: string;
  label?: string;
  ring?: "inner" | "outer";
};

interface BuildCircleFeatureOptions {
  id: string;
  latitude: number;
  longitude: number;
  radiusMiles: number;
  steps?: number;
  properties: CircleFeatureProperties;
}

export function buildCircleFeature({
  id,
  latitude,
  longitude,
  radiusMiles,
  steps = 64,
  properties,
}: BuildCircleFeatureOptions): Feature<
  Polygon,
  CircleFeatureProperties & { centerLng: number; centerLat: number; radiusMiles: number }
> {
  const centerLatRad = (latitude * Math.PI) / 180;
  const centerLngRad = (longitude * Math.PI) / 180;
  const angularDistance = radiusMiles / EARTH_RADIUS_MILES;
  const coordinates: [number, number][] = [];

  for (let step = 0; step <= steps; step += 1) {
    const bearing = (2 * Math.PI * step) / steps;
    const pointLat = Math.asin(
      Math.sin(centerLatRad) * Math.cos(angularDistance) +
        Math.cos(centerLatRad) * Math.sin(angularDistance) * Math.cos(bearing)
    );
    const pointLng =
      centerLngRad +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(centerLatRad),
        Math.cos(angularDistance) - Math.sin(centerLatRad) * Math.sin(pointLat)
      );

    coordinates.push([(pointLng * 180) / Math.PI, (pointLat * 180) / Math.PI]);
  }

  if (
    coordinates.length &&
    (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
      coordinates[0][1] !== coordinates[coordinates.length - 1][1])
  ) {
    coordinates.push([...coordinates[0]]);
  }

  return {
    type: "Feature",
    id,
    properties: {
      ...properties,
      centerLng: longitude,
      centerLat: latitude,
      radiusMiles,
    },
    geometry: {
      type: "Polygon",
      coordinates: [coordinates],
    },
  };
}
